import { useEffect } from "react";
import { useEditor, useValue } from "tldraw";

import { GridlineGhost } from "@/sketch/gridline-ghost";
import { OriginMarker } from "@/sketch/OriginMarker";
import { ScaleBadge } from "@/sketch/ScaleBadge";

/**
 * Screen-space overlays: the scale badge (bottom right) and, while a
 * drafting tool is active, a hint line spelling out the gestures. The
 * gridline tool's shift-click was invisible without it.
 */
const TOOL_HINTS: Record<string, string> = {
  gridline:
    "Click: vertical gridline (A, B…)  ·  Shift-click: horizontal (1, 2…)  ·  Esc: done",
  dim: "Click what to measure: an edge on its own, or one reference after another to string them  ·  then click where the dim line goes (Enter to let the sheet place it)  ·  Esc: cancel",
  callout:
    "Click what the note is about  ·  then click where the lettering goes  ·  type the note (a tag holds a keynote key)  ·  Esc: finish",
};

function ToolHint() {
  const editor = useEditor();
  const toolId = useValue("tool", () => editor.getCurrentToolId(), [editor]);
  const hint = TOOL_HINTS[toolId];
  if (!hint) return null;
  return (
    <div
      style={{
        position: "absolute",
        bottom: 8,
        left: "50%",
        transform: "translateX(-50%)",
        pointerEvents: "none",
        fontFamily: "var(--tl-font-draw)",
        fontSize: 12,
        color: "var(--tl-color-text-1)",
        background: "var(--tl-color-panel)",
        border: "1px solid var(--tl-color-divider)",
        borderRadius: 4,
        padding: "2px 10px",
        whiteSpace: "nowrap",
      }}
    >
      {hint}
    </div>
  );
}

/** Marks the tldraw container so sketch.css's drafting stroke styles apply
 *  only to sketch surfaces, not any other tldraw instance. */
function SketchCssFlags() {
  const editor = useEditor();
  useEffect(() => {
    editor.getContainer().setAttribute("data-sketch", "");
  }, [editor]);
  return null;
}

export function SketchOverlays() {
  return (
    <>
      <SketchCssFlags />
      <ScaleBadge />
      <ToolHint />
    </>
  );
}

/** Page-space companions (the OnTheCanvas slot). */
export function SketchCanvasLayer() {
  return (
    <>
      <OriginMarker />
      <GridlineGhost />
    </>
  );
}
